import { useNavigate, useParams } from 'react-router-dom';
import { OperatorRoutedModalContent } from '../../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderActionCloseButtons } from '../../layout/components/HeaderActionCloseButtons.tsx';
import {
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../../layout/components/form/FormLayout.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import {
    useServerFetch,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import {
    FrontendTariffDefinition,
    InheritMode,
    MaxAmountPerDayConfig,
    MaxAmountType,
} from 'taxomex-shared/tariff/models/TariffDefinition.ts';
import { getHolidayByKey } from 'taxomex-shared/tariff/logic/Holidays.ts';
import { css } from '@emotion/css';
import { OperatorTypo } from '../../ui/OperatorTypo.ts';
import {
    MultiTransactionLimitConfig,
    MultiTransactionLimitLevel,
    MultiTransactionLimitType,
} from 'taxomex-shared/tariff/models/TariffData.ts';
import { TariffCompatibility } from 'taxomex-shared/tariff/models/TariffCompatibility.ts';
import { Box, Stack } from '@mui/material';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { DateTime } from 'luxon';
import { Control, Controller, Path, useForm } from 'react-hook-form';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { type JSX, useEffect, useRef } from 'react';
import { currencyCentsToLocalPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { DivideBar } from '../../parkingabo-users/OperatorParkingaboUsersForm.tsx';

interface TariffDetails {
    id: number;
    name: string;
    tariffDefinition: FrontendTariffDefinition;
    multiTransactionLimit: MultiTransactionLimitConfig | null;
    maxAmountPerDayConfig: MaxAmountPerDayConfig | null;
    tariffCompatibility: TariffCompatibility;
    zoneUses: ZoneInfo[];
}

interface ZoneInfo {
    id: number;
    name: string;
    city: string;
}

const enum CalculatorFields {
    validFrom = 'validFrom',
    validFromHourOfDay = 'validFromHourOfDay',
    validTo = 'validTo',
    validToHourOfDay = 'validToHourOfDay',
    amountChf = 'amountChf',
}

interface OnstreetCalculatorFormFields extends CalculatorFormFieldsBase {
    [CalculatorFields.amountChf]: string | null;
}

interface BarrierGateCalculatorFormFields extends CalculatorFormFieldsBase {
    [CalculatorFields.validTo]: DateTime;
    [CalculatorFields.validToHourOfDay]: string;
}

interface CalculatorFormFieldsBase {
    [CalculatorFields.validFrom]: DateTime;
    [CalculatorFields.validFromHourOfDay]: string;
}

interface BarrierGateCalculatorResponse {
    duration: string;
    amountRappen: number;
}

interface OnstreetCalculatorResponse {
    duration: string;
    validTo: string;
}

export function TariffDetails() {
    const navigate = useNavigate();
    const urlParams = useParams<{ tariffId: string }>();
    const tariffId = urlParams.tariffId ?? null;
    const [tariffDetailsState] = useServerFetch<
        TariffDetails,
        { tariffId: string },
        null
    >(
        context => ({
            url: `/ui-api/operator-account/tariff/${context.tariffId}`,
        }),
        tariffId ? { tariffId } : null,
    );

    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            backUrl={'..'}
            render={() => (
                <OperatorAsyncLoadedSection
                    requestState={tariffDetailsState}
                    render={tariff => (
                        <OperatorRoutedModalContent
                            header={
                                <HeaderComponent
                                    title={tariff.name}
                                    editMode={false}
                                    buttons={
                                        <HeaderActionCloseButtons
                                            onClose={() => navigate('..')}
                                        />
                                    }
                                />
                            }
                            body={
                                <FormContentLayout>
                                    <FormLeftColumn>
                                        <InheritModeField tariff={tariff} />
                                        <ReadOnlyTextField
                                            label={
                                                <Localized
                                                    de="Feiertage"
                                                    fr="Jours fériés"
                                                    it="Giorni festivi"
                                                    en="Holidays"
                                                />
                                            }
                                            value={
                                                <HolidaysDetails
                                                    tariff={tariff}
                                                />
                                            }
                                        />
                                        {tariff.multiTransactionLimit && (
                                            <ReadOnlyTextField
                                                label={
                                                    <MultiTransactionLimitLabel
                                                        level={
                                                            tariff
                                                                .multiTransactionLimit
                                                                .level
                                                        }
                                                    />
                                                }
                                                value={
                                                    <MultiTransactionLimitDetails
                                                        multiTransactionLimit={
                                                            tariff.multiTransactionLimit
                                                        }
                                                    />
                                                }
                                            />
                                        )}
                                        {tariff.maxAmountPerDayConfig && (
                                            <ReadOnlyTextField
                                                label={
                                                    <Localized
                                                        de="Limite (pro Transaktion)"
                                                        fr="Limite (par transaction)"
                                                        it="Limite (per transazione)"
                                                        en="Limit (per transaction)"
                                                    />
                                                }
                                                value={
                                                    <MaxAmountPerDayConfigDetails
                                                        maxAmountPerDayConfig={
                                                            tariff.maxAmountPerDayConfig
                                                        }
                                                    />
                                                }
                                            />
                                        )}
                                        <ReadOnlyTextField
                                            label={
                                                <Localized
                                                    de="Verwendungen"
                                                    fr="Utilisations"
                                                    it="Utilizzi"
                                                    en="Uses"
                                                />
                                            }
                                            value={
                                                <ZoneUsesDetails
                                                    zoneUses={tariff.zoneUses}
                                                />
                                            }
                                        />
                                    </FormLeftColumn>
                                    <FormRightColumn>
                                        <DivideBar
                                            largeMarginTop={false}
                                            title={
                                                <Localized
                                                    de="Rechner"
                                                    fr="Calculateur"
                                                    it="Calcolatore"
                                                    en="Calculator"
                                                />
                                            }
                                        />
                                        <Box
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <TariffCalculatorColumn
                                                tariff={tariff}
                                            />
                                            <ReadOnlyTextField
                                                label={'ID'}
                                                value={tariff.id}
                                            />
                                        </Box>
                                    </FormRightColumn>
                                </FormContentLayout>
                            }
                        />
                    )}
                />
            )}
        />
    );
}

function ZoneUsesDetails({ zoneUses }: { zoneUses: ZoneInfo[] }) {
    if (zoneUses.length === 0) {
        return <>{EmDash}</>;
    }

    return (
        <>
            {zoneUses.map(zone => (
                <Box key={zone.id}>{`${zone.name} ${EmDash} ${zone.city}`}</Box>
            ))}
        </>
    );
}

function TariffCalculatorColumn({ tariff }: { tariff: TariffDetails }) {
    switch (tariff.tariffCompatibility) {
        case TariffCompatibility.BARRIER_GATE:
            return <BarrierGateTariffCalculator tariff={tariff} />;
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL:
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL_AND_TOMECO:
            return <OnstreetTariffCalculator tariff={tariff} />;
    }
}

function BarrierGateTariffCalculator({ tariff }: { tariff: TariffDetails }) {
    const [calculateTariffState, calculateTariff] = useServerWrite<
        {
            tariffDefinition: FrontendTariffDefinition;
            tariffId: number;
        } & BarrierGateCalculatorFormFields,
        BarrierGateCalculatorResponse,
        null
    >(() => ({
        url: `/ui-api/operator-account/tariff/calculate/barrier-gate`,
    }));
    const calculateTariffRef = useRef(calculateTariff);

    useEffect(() => {
        calculateTariffRef.current = calculateTariff;
    }, [calculateTariff]);

    const now = DateTime.now();
    const { control, handleSubmit, watch } =
        useForm<BarrierGateCalculatorFormFields>({
            defaultValues: {
                [CalculatorFields.validFrom]: now,
                [CalculatorFields.validFromHourOfDay]: now.toFormat('HH:mm'),
                [CalculatorFields.validTo]: now,
                [CalculatorFields.validToHourOfDay]: now
                    .plus({ hours: 1 })
                    .toFormat('HH:mm'),
            },
            mode: 'onBlur',
        });

    // eslint-disable-next-line react-hooks/incompatible-library
    const validFrom = watch(CalculatorFields.validFrom);

    const validFromHourOfDay = watch(CalculatorFields.validFromHourOfDay);

    const validTo = watch(CalculatorFields.validTo);

    const validToHourOfDay = watch(CalculatorFields.validToHourOfDay);

    useEffect(() => {
        if (validFrom && validFromHourOfDay && validTo && validToHourOfDay) {
            handleSubmit(values => {
                calculateTariffRef.current({
                    tariffId: tariff.id,
                    tariffDefinition: tariff.tariffDefinition,
                    [CalculatorFields.validFrom]:
                        values[CalculatorFields.validFrom],
                    [CalculatorFields.validFromHourOfDay]:
                        values[CalculatorFields.validFromHourOfDay],
                    [CalculatorFields.validTo]:
                        values[CalculatorFields.validTo],
                    [CalculatorFields.validToHourOfDay]:
                        values[CalculatorFields.validToHourOfDay],
                });
            })();
        }
    }, [
        handleSubmit,
        tariff.id,
        tariff.tariffDefinition,
        validFrom,
        validFromHourOfDay,
        validTo,
        validToHourOfDay,
    ]);

    return (
        <Box>
            <ValidFromSelection control={control} />

            <Stack
                spacing={2}
                direction="row"
                sx={{
                    alignItems: 'flex-end',
                    marginTop: '24px',
                }}
            >
                <Controller
                    name={CalculatorFields.validTo}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            label={
                                <Localized
                                    de="Endzeit"
                                    fr="Heure de fin"
                                    it="Orario fine"
                                    en="End time"
                                />
                            }
                            value={field.value}
                            onChange={field.onChange}
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                },
                            }}
                        />
                    )}
                    rules={{ required: true }}
                />
                <ReactHookFormTextField
                    name={CalculatorFields.validToHourOfDay}
                    control={control}
                    type={'time'}
                    trimOnBlur={true}
                    rules={{ required: true }}
                />
            </Stack>
            <OperatorAsyncLoadedSection
                requestState={calculateTariffState}
                renderError={() => (
                    <BackendRequestErrorMessage
                        requestState={calculateTariffState}
                    />
                )}
                render={(result: BarrierGateCalculatorResponse) => (
                    <Box sx={{ marginTop: '24px' }}>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={result.duration}
                        />
                        <Box sx={{ marginTop: '24px' }}>
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Betrag (CHF)"
                                        fr="Montant (CHF)"
                                        it="Importo (CHF)"
                                        en="Amount (CHF)"
                                    />
                                }
                                value={currencyCentsToLocalPrice(
                                    result.amountRappen,
                                )}
                            />
                        </Box>
                    </Box>
                )}
            />
        </Box>
    );
}

function OnstreetTariffCalculator({ tariff }: { tariff: TariffDetails }) {
    const [calculateTariffState, calculateTariff] = useServerWrite<
        {
            tariffDefinition: FrontendTariffDefinition;
            tariffId: number;
        } & OnstreetCalculatorFormFields,
        OnstreetCalculatorResponse,
        null
    >(() => ({
        url: `/ui-api/operator-account/tariff/calculate/onstreet`,
    }));
    const calculateTariffRef = useRef(calculateTariff);

    useEffect(() => {
        calculateTariffRef.current = calculateTariff;
    }, [calculateTariff]);

    const now = DateTime.now();
    const { control, handleSubmit, watch } =
        useForm<OnstreetCalculatorFormFields>({
            defaultValues: {
                [CalculatorFields.validFrom]: now,
                [CalculatorFields.validFromHourOfDay]: now.toFormat('HH:mm'),
                [CalculatorFields.amountChf]: '1.00',
            },
            mode: 'onBlur',
        });

    // eslint-disable-next-line react-hooks/incompatible-library
    const validFrom = watch(CalculatorFields.validFrom);

    const validFromHourOfDay = watch(CalculatorFields.validFromHourOfDay);

    const amount = watch(CalculatorFields.amountChf);

    useEffect(() => {
        if (validFrom && validFromHourOfDay && amount) {
            handleSubmit(values => {
                calculateTariffRef.current({
                    tariffId: tariff.id,
                    tariffDefinition: tariff.tariffDefinition,
                    [CalculatorFields.validFrom]:
                        values[CalculatorFields.validFrom],
                    [CalculatorFields.validFromHourOfDay]:
                        values[CalculatorFields.validFromHourOfDay],
                    [CalculatorFields.amountChf]:
                        values[CalculatorFields.amountChf],
                });
            })();
        }
    }, [
        amount,
        handleSubmit,
        tariff.id,
        tariff.tariffDefinition,
        validFrom,
        validFromHourOfDay,
    ]);

    return (
        <Box>
            <ValidFromSelection control={control} />
            <Box sx={{ marginTop: '24px' }}>
                <ReactHookFormTextField
                    label={
                        <Localized
                            de="Betrag (CHF)"
                            fr="Montant (CHF)"
                            it="Importo (CHF)"
                            en="Amount (CHF)"
                        />
                    }
                    name={CalculatorFields.amountChf}
                    control={control}
                    type={'text'}
                    trimOnBlur={true}
                    rules={{ required: true }}
                />
            </Box>
            <OperatorAsyncLoadedSection
                requestState={calculateTariffState}
                renderError={() => (
                    <BackendRequestErrorMessage
                        requestState={calculateTariffState}
                    />
                )}
                render={(result: OnstreetCalculatorResponse) => (
                    <Box sx={{ marginTop: '24px' }}>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={result.duration}
                        />
                        <Box sx={{ marginTop: '24px' }}>
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Gültig bis"
                                        fr="Valable jusqu'au"
                                        it="Valida fino a"
                                        en="Valid until"
                                    />
                                }
                                value={Formatter.dayMonthYearHourMinute(
                                    DateTime.fromISO(result.validTo),
                                )}
                            />
                        </Box>
                    </Box>
                )}
            />
        </Box>
    );
}

function ValidFromSelection<T extends CalculatorFormFieldsBase>({
    control,
}: {
    control: Control<T, object>;
}) {
    return (
        <Stack
            spacing={2}
            direction="row"
            sx={{
                alignItems: 'flex-end',
                marginTop: '24px',
            }}
        >
            <Controller
                name={CalculatorFields.validFrom as Path<T>}
                control={control}
                render={({ field, fieldState }) => (
                    <DesktopDatePicker
                        label={
                            <Localized
                                de="Startzeit"
                                fr="Heure de début"
                                it="Ora inizio"
                                en="Start time"
                            />
                        }
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                            textField: {
                                error: !!fieldState.error,
                                helperText: fieldState.error?.message,
                            },
                        }}
                    />
                )}
                rules={{ required: true }}
            />
            <ReactHookFormTextField
                name={CalculatorFields.validFromHourOfDay as Path<T>}
                control={control}
                type={'time'}
                trimOnBlur={true}
                rules={{ required: true }}
            />
        </Stack>
    );
}

function MultiTransactionLimitLabel({
    level,
}: {
    level: MultiTransactionLimitLevel;
}) {
    switch (level) {
        case MultiTransactionLimitLevel.CUSTOMER:
            return (
                <Localized
                    de="Limite (pro Benutzerkonto)"
                    fr="Limite (par compte utilisateur)"
                    it="Limite (per conto utente)"
                    en="Limit (per customer account)"
                />
            );
        case MultiTransactionLimitLevel.ENTITY:
            return (
                <Localized
                    de="Limite (pro Fahrzeug)"
                    fr="Limite (par véhicule)"
                    it="Limite (per veicolo)"
                    en="Limit (per vehicle)"
                />
            );
    }
}

function MaxAmountPerDayConfigDetails({
    maxAmountPerDayConfig,
}: {
    maxAmountPerDayConfig: MaxAmountPerDayConfig;
}) {
    return (
        <Box>
            {maxAmountPerDayConfig.rappen}
            <Localized
                de=" Rappen "
                fr=" centimes "
                it=" centesimi "
                en=" cents "
            />
            <MaxAmountTypeText type={maxAmountPerDayConfig.type} />
        </Box>
    );
}

function MaxAmountTypeText({ type }: { type: MaxAmountType }) {
    switch (type) {
        case MaxAmountType._24h:
            return (
                <Localized
                    de="pro 24h"
                    fr="par 24h"
                    it="ogni 24h"
                    en="per 24h"
                />
            );
        case MaxAmountType.calendarDay:
            return (
                <Localized
                    de="pro Kalendertag"
                    fr="par jour calendaire"
                    it="al giorno di calendario"
                    en="per calendar day"
                />
            );
    }
}

function MultiTransactionLimitDetails({
    multiTransactionLimit,
}: {
    multiTransactionLimit: MultiTransactionLimitConfig;
}) {
    return (
        <>
            <Box>
                {multiTransactionLimit.limit1.amountRappen}
                <Localized
                    de=" Rappen "
                    fr=" centimes "
                    it=" centesimi "
                    en=" cents "
                />
                <MultiTransactionLimitTypeText
                    type={multiTransactionLimit.limit1.type}
                />
            </Box>
            {multiTransactionLimit.limit2 && (
                <Box>
                    {multiTransactionLimit.limit2.amountRappen}
                    <Localized
                        de=" Rappen "
                        fr=" centimes "
                        it=" centesimi "
                        en=" cents "
                    />
                    <MultiTransactionLimitTypeText
                        type={multiTransactionLimit.limit2.type}
                    />
                </Box>
            )}
        </>
    );
}

function MultiTransactionLimitTypeText({
    type,
}: {
    type: MultiTransactionLimitType;
}) {
    switch (type) {
        case MultiTransactionLimitType.LAST_24H:
            return (
                <Localized
                    de="letzte 24h"
                    fr="derniers 24h"
                    it="ultimi 24h"
                    en="last 24h"
                />
            );
        case MultiTransactionLimitType.DAY:
            return (
                <Localized
                    de="pro Kalendertag"
                    fr="par jour calendaire"
                    it="al giorno di calendario"
                    en="per calendar day"
                />
            );
        case MultiTransactionLimitType.MONTH:
            return (
                <Localized
                    de="pro Monat"
                    fr="par mois"
                    it="al mese"
                    en="per month"
                />
            );
    }
}

function InheritModeDescription({ inheritMode }: { inheritMode: InheritMode }) {
    switch (inheritMode) {
        case 'inheritTime':
            return (
                <Localized
                    de="Erbzeit"
                    fr="Hériter le temps"
                    it="Eredita il tempo"
                    en="Inherit Time"
                />
            );
        case 'reset':
            return (
                <Localized
                    de="Zurückstellung"
                    fr="Remettre"
                    it="Azzera"
                    en="Reset"
                />
            );
    }
}

function InheritModeField({
    tariff,
}: {
    tariff: TariffDetails;
}): JSX.Element | null {
    switch (tariff.tariffCompatibility) {
        case TariffCompatibility.BARRIER_GATE:
            return null;
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL:
        case TariffCompatibility.ZONE_ENFORCED_DIGITAL_AND_TOMECO:
            return (
                <ReadOnlyTextField
                    label={
                        <Localized
                            de="Erbmodus"
                            fr="Mode d'héritage"
                            it="Modo ereditario"
                            en="Inherit mode"
                        />
                    }
                    value={
                        <InheritModeDescription
                            inheritMode={tariff.tariffDefinition.inheritMode}
                        />
                    }
                />
            );
    }
}

function HolidaysDetails({ tariff }: { tariff: TariffDetails }): JSX.Element {
    const { language } = useLanguage();
    const holidays = tariff.tariffDefinition.holidays;
    if (holidays.length === 0) {
        return (
            <Localized
                de="Keine Feiertage"
                fr="Pas de jours fériés"
                it="Nessun giorno festivo"
                en="No holidays"
            />
        );
    }

    return (
        <Box>
            {holidays.map(holiday => {
                const dayProfileName = getDayProfileNameById(
                    tariff,
                    holiday.dayIdRef,
                );
                const holidayText = getHolidayByKey(holiday.holidayKey).text(
                    language,
                );
                return (
                    <Box key={holiday.holidayKey}>
                        {holidayText}
                        {dayProfileName && (
                            <span
                                className={css({
                                    ...OperatorTypo.body,
                                    marginLeft: '3px',
                                })}
                            >
                                ({dayProfileName})
                            </span>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

const getDayProfileNameById = (
    tariff: TariffDetails,
    holidayDayProfileId: number,
): string | undefined => {
    for (const day of tariff.tariffDefinition.days) {
        if (day.dayProfileId === holidayDayProfileId) {
            return day.name;
        }
    }
};
